import React, { memo } from "react";
import { Link } from "react-router-dom";
import "../styles/product-card.css";

/* ================================
   Configuration (Enterprise-ready)
================================ */

const IMAGE_BASE_URL = "https://auth.afrogebeya.com/uploads/products";

const PLACEHOLDER_IMAGE = "https://via.placeholder.com/300x300?text=No+Image";

const resolveProductImage = (product) => {
  const storedName = product?.mainImageUrl || product?.mainImage?.storedName;

  return storedName
    ? `${IMAGE_BASE_URL}/${storedName.replace(/\\/g, "/")}`
    : PLACEHOLDER_IMAGE;
};

const formatPrice = (value, currency) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);

/* ================================
   Component
================================ */

const ProductCard = ({ product }) => {
  if (!product) return null;

  const {
    id,
    productName,
    currency = "USD",
    price = 0,
    discountPrice,
    averageRating = 0,
    totalRatings = 0,
    availableQuantity = 0,
    fastShipping,
  } = product;

  const imageUrl = resolveProductImage(product);

  const hasDiscount =
    typeof discountPrice === "number" &&
    discountPrice > 0 &&
    discountPrice < price;

  const rating = Number(averageRating).toFixed(1);
  const roundedRating = Math.round(averageRating);

  const isOutOfStock = availableQuantity === 0;

  return (
    <article
      className={`product-card ${isOutOfStock ? "product-card--disabled" : ""}`}
      aria-label={productName}
    >
      {/* ================= Image ================= */}
      <Link
        to={`/products/${id}`}
        className="product-image-wrapper"
        aria-label={`View details for ${productName}`}
      >
        <img
          src={imageUrl}
          alt={productName}
          loading="lazy"
          className="product-image"
        />

        {isOutOfStock && (
          <span className="badge badge-danger">Out of stock</span>
        )}

        {fastShipping && !isOutOfStock && (
          <span className="badge badge-success">Fast delivery</span>
        )}
      </Link>

      {/* ================= Content ================= */}
      <div className="product-content">
        {/* Title */}
        <h3 className="product-title" title={productName}>
          <Link to={`/products/${id}`}>{productName}</Link>
        </h3>

        {/* Rating */}
        <div className="product-rating" aria-label={`Rated ${rating} out of 5`}>
          {totalRatings > 0 ? (
            <>
              <span className="stars">
                {"★".repeat(roundedRating)}
                {"☆".repeat(5 - roundedRating)}
              </span>
              <span className="rating-count">({totalRatings})</span>
            </>
          ) : (
            <span className="rating-empty">No reviews yet</span>
          )}
        </div>

        {/* Price */}
        <div className="product-price">
          {hasDiscount ? (
            <>
              <span className="price-discount">
                {formatPrice(discountPrice, currency)}
              </span>
              <span className="price-original">
                {formatPrice(price, currency)}
              </span>
            </>
          ) : (
            <span className="price-normal">{formatPrice(price, currency)}</span>
          )}
        </div>

        {/* CTA */}
        <Link
          to={`/products/${id}`}
          className={`btn btn-sm w-100 ${isOutOfStock ? "btn-disabled" : ""}`}
          aria-disabled={isOutOfStock}
        >
          View product
        </Link>
      </div>
    </article>
  );
};

export default memo(ProductCard);
